import { useEffect, useState } from 'react';

import {
    classInfoDemoData,
    schoolSettingsDemoData,
    makeClassAttendance,
    classSettingsDemoData,
    studentsDemoData,
    makeClassSessions,
    makeAttendanceData,
    makeSessionMetricData,
    makeClassMetrics
} from './index';
import { type SessionAttendanceSummary } from '../../components/attendance/types';
import { type ClassMetrics, type SessionMetrics } from '../../components/dashboard/types';
import { type SchoolSettings } from '../../types/apiTypes';
import { type ClassSession, type ClassStudent } from '../types';

interface SessionMetricObject {
    [key: string]: SessionMetrics;
}

interface SessionAttendanceObject {
    [key: string]: SessionAttendanceSummary;
}

interface UseDemoDataProps {
    isDemo: boolean;
    selectedSessionUuid: string | null;

    sessions: ClassSession[];
    setSessions: React.Dispatch<React.SetStateAction<ClassSession[]>>;

    setStudents: React.Dispatch<React.SetStateAction<ClassStudent[]>>;
    setClassInfo;
    setSchoolSettings;
    setClassMetrics: React.Dispatch<React.SetStateAction<ClassMetrics | undefined>>;
    setClassAttendance: React.Dispatch<React.SetStateAction<SessionAttendanceSummary[] | undefined>>;
    setClassSettings: React.Dispatch<React.SetStateAction<SchoolSettings | undefined>>;
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setAttendance: React.Dispatch<React.SetStateAction<SessionAttendanceSummary | undefined>>;
    setSessionMetrics: (sessionMetrics: any) => void;
}

interface InitializeStatsProps {
    sessions: ClassSession[];
    students: ClassStudent[];
}

export const useDemoData = ({
    isDemo,
    setStudents,
    setClassInfo,
    setSchoolSettings,
    setClassMetrics,
    setClassAttendance,
    setClassSettings,
    setSessions,
    sessions: classSessions,
    selectedSessionUuid,
    setAttendance,
    isLoading,
    setIsLoading,
    setSessionMetrics
}: UseDemoDataProps) => {
    const [sessionAttendanceObject, setSessionAttendanceObject] = useState<SessionAttendanceObject | null>(null);
    const [sessionMetricsObject, setSessionMetricsObject] = useState<SessionMetricObject | null>(null);

    const initializeStats = ({ sessions, students }: InitializeStatsProps) => {
        const attendance: SessionAttendanceObject = {};
        const sessionMetrics = {};

        sessions.forEach((session) => {
            const sessionAttendance = makeAttendanceData({ students, session });
            attendance[session.session_id] = sessionAttendance;

            const sessionMetricsFromStudents = makeSessionMetricData({ students, sessionAttendance });
            sessionMetrics[session.session_id] = sessionMetricsFromStudents;
        });

        setSessionAttendanceObject(attendance);
        setSessionMetricsObject(sessionMetrics);
    };

    useEffect(() => {
        if (isDemo && isLoading) {
            setStudents(studentsDemoData);
            setClassInfo(classInfoDemoData);
            setSchoolSettings(schoolSettingsDemoData);

            const sessions = makeClassSessions({ count: 12 });
            initializeStats({ sessions, students: studentsDemoData });

            setClassSettings(classSettingsDemoData);
            setSessions(sessions);

            setIsLoading(false);
        }
    }, [
        isDemo,
        isLoading,
        sessionAttendanceObject,
        sessionMetricsObject,
        setClassAttendance,
        setClassInfo,
        setClassMetrics,
        setClassSettings,
        setIsLoading,
        setSchoolSettings,
        setSessions,
        setStudents
    ]);

    useEffect(() => {
        if (isDemo && sessionMetricsObject) {
            const classMetrics = makeClassMetrics({ sessionMetricsObject, students: studentsDemoData });
            setClassMetrics(classMetrics);
        }
    }, [isDemo, sessionMetricsObject, setClassMetrics]);

    useEffect(() => {
        if (isDemo && sessionAttendanceObject) {
            const classAttendance = makeClassAttendance({ sessionAttendanceObject, students: studentsDemoData });
            setClassAttendance(classAttendance);
        }
    }, [isDemo, sessionAttendanceObject, setClassAttendance]);

    useEffect(() => {
        if (isDemo && selectedSessionUuid && sessionAttendanceObject && sessionMetricsObject) {
            const selectedAttendance = sessionAttendanceObject[selectedSessionUuid];
            if (selectedAttendance) {
                setAttendance(selectedAttendance);
            }

            const selectedMetric = sessionMetricsObject[selectedSessionUuid];
            if (selectedMetric) {
                setSessionMetrics(selectedMetric);
            }
        }
    }, [
        isDemo,
        selectedSessionUuid,
        sessionAttendanceObject,
        sessionMetricsObject,
        classSessions,
        setAttendance,
        setSessionMetrics
    ]);
};
